/*
 * Purpose: Workflow state — create, edit, persist, undo/redo, execute flows
 * Last modified: 2026-06-28 | Agent
 * Completeness: 94
 */
import { create } from 'zustand';
import { WorkflowExecutor } from '@/services/executor';
import type { ExecutionContext } from '@/services/executor';
import type { ExecutionOptions } from '@/services/executor';
import { loadWorkflowsFromStorage, persistWorkflows, createRunnableBlankWorkflow } from '@/lib/workflowUtils';
import { normalizeWorkflowMissionGraph } from '@/lib/missionAnchors';
import { useAuthStore } from '@/stores/authStore';
import {
  assertCanCreateMission,
  assertCanRunFlows,
  assertCanSaveFlows,
} from '@/lib/subscriptionEnforce';
import { canSaveFlows, subscriptionFromState } from '@/lib/subscriptionPolicy';
import {
  formatMapFlowLayout,
  type MapFlowFormatMode,
  type ResolvedMapFlowOrientation,
} from '@/lib/mapFlowOrientation';
import { autoConfigureWorkflowNodes } from '@/services/blockAutoConfigure';

export type TriggerType = 'webhook' | 'timer' | 'manual' | 'api' | 'gmail_inbound';
export type ToolType = 'http' | 'transform' | 'conditional' | 'delay' | 'mcp_tool';
export type ActionType = 'notification' | 'log' | 'webhook' | 'storage';
export type GoalType = 'outcome';
export type StartType = 'mission';
export type AgentType = 'assistant';
export type DroneType = 'remote';
export type TubeType = 'journey';
export type BrandedType =
  | 'bdna_lock'
  | 'squadbox_mega'
  | 'thrpy_dictation'
  | 'sensaffect_emotion';

export type MapFlowOrientation = 'horizontal' | 'vertical' | 'auto';

export interface MissionMeta {
  /** When true, block map cannot change during a run */
  mapLocked?: boolean;
  /** When true (default), AI may replan operational blocks mid-run */
  allowDynamicMap?: boolean;
  /** Map flow axis — auto picks vertical on narrow/portrait viewports */
  flowOrientation?: MapFlowOrientation;
  /** Toggle inline flow tracing overlays/highlights while editing */
  flowTracingMode?: boolean;
  /** Smart Blocks: auto-configure nodes immediately when links are created */
  smartAutoConfigureOnConnect?: boolean;
  /** Smart Blocks flow-level auto-fix behavior */
  smartAutoFixPolicy?: 'safe-only' | 'safe-and-review';
  /** Smart Blocks input modal behavior for human-required fields */
  smartInputModalBehavior?: 'auto-open' | 'badge-only';
  /** Truth factor strictness for intent/outcome matching */
  truthFactorStrictness?: 'lenient' | 'balanced' | 'strict';
  lastDynamicPatchAt?: string;
  lastPatchRationale?: string;
}

export interface Node {
  id: string;
  type: 'start' | 'trigger' | 'tool' | 'action' | 'goal' | 'agent' | 'drone' | 'tube' | 'branded';
  subtype:
    | StartType
    | TriggerType
    | ToolType
    | ActionType
    | GoalType
    | AgentType
    | DroneType
    | TubeType
    | BrandedType;
  config: Record<string, unknown>;
  x: number;
  y: number;
}

export interface Edge {
  id: string;
  from: string;
  to: string;
  /** journey_tube = open path from START toward GOAL while outcome unfolds naturally */
  kind?: 'link' | 'journey_tube';
}

export interface Workflow {
  id: string;
  name: string;
  nodes: Node[];
  edges: Edge[];
  createdAt: Date;
  updatedAt: Date;
  missionMeta?: MissionMeta;
}

interface MapUndoState {
  mapUndoPast: MapGraphSnapshot[];
  mapUndoFuture: MapGraphSnapshot[];
  mapUndoWorkflowId: string | null;
}

interface WorkflowStore extends MapUndoState {
  workflows: Workflow[];
  currentWorkflow: Workflow | null;
  selectedNode: string | null;
  /** Source node when linking blocks on the map (+ then click target) */
  connectFromNodeId: string | null;

  createWorkflow: (name: string) => void;
  cloneWorkflow: (workflowId: string, newName: string) => void;
  deleteWorkflow: (workflowId: string) => void;
  setCurrentWorkflow: (workflow: Workflow) => void;
  addNode: (node: Node) => void;
  updateNode: (id: string, updates: Record<string, unknown>) => void;
  deleteNode: (id: string) => void;
  addEdge: (from: string, to: string) => void;
  deleteEdge: (id: string) => void;
  selectNode: (id: string | null) => void;
  beginConnect: (fromNodeId: string) => void;
  cancelConnect: () => void;
  completeConnect: (toNodeId: string) => 'linked' | 'unlinked' | null;
  autoConfigureNode: (nodeId: string) => string[];
  saveWorkflow: () => void;
  updateWorkflowName: (name: string) => void;
  updateMissionMeta: (patch: Partial<MissionMeta>) => void;
  /** Re-position nodes — non-destructive reorient/tidy (see formatMapFlowLayout) */
  applyFlowLayout: (
    orientation: ResolvedMapFlowOrientation,
    options?: {
      recordUndo?: boolean;
      mode?: MapFlowFormatMode;
      fromOrientation?: ResolvedMapFlowOrientation;
    }
  ) => void;
  executeWorkflow: (signal?: AbortSignal, options?: Omit<ExecutionOptions, 'signal'>) => Promise<ExecutionContext[]>;
  loadWorkflows: () => void;
  reloadFromTenantStorage: () => void;
  importWorkflow: (workflow: Workflow) => void;
  /** Replace current flow graph from AI plan (keeps flow id when one is selected) */
  applyAssembledWorkflow: (
    assembled: Workflow,
    options?: { persist?: boolean; skipUndo?: boolean }
  ) => boolean;
  /** Apply only when the expected workflow is still selected (returns false if user switched flows) */
  applyAssembledWorkflowIfCurrent: (
    expectedWorkflowId: string | null | undefined,
    assembled: Workflow,
    options?: { persist?: boolean; skipUndo?: boolean }
  ) => boolean;

  /** Snapshot current map before a structural edit (AI apply, add/delete, etc.) */
  recordMapUndoPoint: () => void;
  undoMap: () => boolean;
  redoMap: () => boolean;
  canUndoMap: () => boolean;
  canRedoMap: () => boolean;
}

const generateId = () => Math.random().toString(36).slice(2, 11);

const MAX_MAP_UNDO = 50;

type MapGraphSnapshot = {
  nodes: Node[];
  edges: Edge[];
  name: string;
  missionMeta?: MissionMeta;
};

function cloneMapSnapshot(workflow: Workflow): MapGraphSnapshot {
  return {
    nodes: structuredClone(workflow.nodes),
    edges: structuredClone(workflow.edges),
    name: workflow.name,
    missionMeta: workflow.missionMeta ? { ...workflow.missionMeta } : undefined,
  };
}

function snapshotsEqual(a: MapGraphSnapshot, b: MapGraphSnapshot): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function resetMapUndoState() {
  return { mapUndoPast: [] as MapGraphSnapshot[], mapUndoFuture: [] as MapGraphSnapshot[], mapUndoWorkflowId: null as string | null };
}

const initialWorkflows = loadWorkflowsFromStorage();

function canPersistWorkflows(): boolean {
  return canSaveFlows(subscriptionFromState(useAuthStore.getState().serverUserState));
}

function guardedPersist(workflows: Workflow[]): void {
  if (canPersistWorkflows()) persistWorkflows(workflows);
}

function syncCurrentWorkflow(
  workflows: Workflow[],
  currentWorkflow: Workflow | null
): { workflows: Workflow[]; currentWorkflow: Workflow | null } {
  if (!currentWorkflow) {
    guardedPersist(workflows);
    return { workflows, currentWorkflow };
  }

  const exists = workflows.some((w) => w.id === currentWorkflow.id);
  const nextWorkflows = exists
    ? workflows.map((w) => (w.id === currentWorkflow.id ? currentWorkflow : w))
    : [...workflows, currentWorkflow];

  guardedPersist(nextWorkflows);
  return { workflows: nextWorkflows, currentWorkflow };
}

export const useWorkflowStore = create<WorkflowStore>((set, get) => ({
  workflows: initialWorkflows,
  currentWorkflow: initialWorkflows[0] ?? null,
  selectedNode: null,
  connectFromNodeId: null,
  ...resetMapUndoState(),

  recordMapUndoPoint: () => {
    const { currentWorkflow, mapUndoPast, mapUndoWorkflowId } = get();
    if (!currentWorkflow) return;

    const snap = cloneMapSnapshot(currentWorkflow);
    let past = mapUndoWorkflowId === currentWorkflow.id ? mapUndoPast : [];
    const last = past[past.length - 1];
    if (last && snapshotsEqual(last, snap)) return;

    past = [...past, snap].slice(-MAX_MAP_UNDO);
    set({ mapUndoPast: past, mapUndoFuture: [], mapUndoWorkflowId: currentWorkflow.id });
  },

  canUndoMap: () => {
    const s = get();
    return Boolean(
      s.currentWorkflow &&
        s.mapUndoWorkflowId === s.currentWorkflow.id &&
        s.mapUndoPast.length > 0
    );
  },

  canRedoMap: () => {
    const s = get();
    return Boolean(
      s.currentWorkflow &&
        s.mapUndoWorkflowId === s.currentWorkflow.id &&
        s.mapUndoFuture.length > 0
    );
  },

  undoMap: () => {
    const s = get();
    if (!s.currentWorkflow || s.mapUndoPast.length === 0 || s.mapUndoWorkflowId !== s.currentWorkflow.id) {
      return false;
    }

    const currentSnap = cloneMapSnapshot(s.currentWorkflow);
    const prev = s.mapUndoPast[s.mapUndoPast.length - 1];
    const restored: Workflow = {
      ...s.currentWorkflow,
      nodes: prev.nodes,
      edges: prev.edges,
      name: prev.name,
      missionMeta: prev.missionMeta,
      updatedAt: new Date(),
    };

    set({
      ...syncCurrentWorkflow(s.workflows, restored),
      mapUndoPast: s.mapUndoPast.slice(0, -1),
      mapUndoFuture: [...s.mapUndoFuture, currentSnap],
      mapUndoWorkflowId: s.currentWorkflow.id,
      selectedNode: null,
      connectFromNodeId: null,
    });
    return true;
  },

  redoMap: () => {
    const s = get();
    if (!s.currentWorkflow || s.mapUndoFuture.length === 0 || s.mapUndoWorkflowId !== s.currentWorkflow.id) {
      return false;
    }

    const currentSnap = cloneMapSnapshot(s.currentWorkflow);
    const next = s.mapUndoFuture[s.mapUndoFuture.length - 1];
    const restored: Workflow = {
      ...s.currentWorkflow,
      nodes: next.nodes,
      edges: next.edges,
      name: next.name,
      missionMeta: next.missionMeta,
      updatedAt: new Date(),
    };

    set({
      ...syncCurrentWorkflow(s.workflows, restored),
      mapUndoPast: [...s.mapUndoPast, currentSnap],
      mapUndoFuture: s.mapUndoFuture.slice(0, -1),
      mapUndoWorkflowId: s.currentWorkflow.id,
      selectedNode: null,
      connectFromNodeId: null,
    });
    return true;
  },

  loadWorkflows: () => {
    const workflows = loadWorkflowsFromStorage();
    set({
      workflows,
      currentWorkflow: get().currentWorkflow ?? workflows[0] ?? null,
    });
  },

  reloadFromTenantStorage: () => {
    const workflows = loadWorkflowsFromStorage();
    const current = get().currentWorkflow;
    const nextCurrent = current
      ? workflows.find((w) => w.id === current.id) ?? workflows[0] ?? null
      : workflows[0] ?? null;
    set({
      workflows,
      currentWorkflow: nextCurrent,
      selectedNode: null,
      connectFromNodeId: null,
    });
  },

  setCurrentWorkflow: (workflow) => {
    set((state) => ({
      currentWorkflow: workflow,
      selectedNode: null,
      connectFromNodeId: null,
      ...(state.mapUndoWorkflowId !== workflow.id ? resetMapUndoState() : {}),
    }));
  },

  createWorkflow: (name) => {
    const { workflows } = get();
    if (!assertCanSaveFlows()) return;
    if (!assertCanCreateMission(workflows.length)) return;

    const newWorkflow = createRunnableBlankWorkflow(name);

    set((state) => {
      const workflows = [...state.workflows, newWorkflow];
      guardedPersist(workflows);
      return { workflows, currentWorkflow: newWorkflow, selectedNode: null, ...resetMapUndoState() };
    });
  },

  cloneWorkflow: (workflowId, newName) => {
    if (!assertCanSaveFlows()) return;
    if (!assertCanCreateMission(get().workflows.length)) return;

    set((state) => {
      const source = state.workflows.find((w) => w.id === workflowId);
      if (!source) return state;

      const idMap = new Map<string, string>();
      const nodes = source.nodes.map((node) => {
        const newId = generateId();
        idMap.set(node.id, newId);
        return { ...node, id: newId };
      });

      const edges = source.edges.map((edge) => ({
        id: generateId(),
        from: idMap.get(edge.from) ?? edge.from,
        to: idMap.get(edge.to) ?? edge.to,
      }));

      const clonedWorkflow: Workflow = normalizeWorkflowMissionGraph({
        id: generateId(),
        name: newName,
        nodes,
        edges,
        createdAt: new Date(),
        updatedAt: new Date(),
        missionMeta: source.missionMeta ? { ...source.missionMeta } : undefined,
      });

      const workflows = [...state.workflows, clonedWorkflow];
      guardedPersist(workflows);
      return { workflows, currentWorkflow: clonedWorkflow, selectedNode: null, ...resetMapUndoState() };
    });
  },

  deleteWorkflow: (workflowId) => {
    if (!assertCanSaveFlows()) return;

    set((state) => {
      const workflows = state.workflows.filter((w) => w.id !== workflowId);
      const currentWorkflow =
        state.currentWorkflow?.id === workflowId ? workflows[0] ?? null : state.currentWorkflow;

      guardedPersist(workflows);
      return { workflows, currentWorkflow, selectedNode: null };
    });
  },

  importWorkflow: (workflow) => {
    if (!assertCanSaveFlows()) return;
    if (!assertCanCreateMission(get().workflows.length)) return;

    set((state) => {
      const imported: Workflow = normalizeWorkflowMissionGraph({
        ...workflow,
        id: generateId(),
        createdAt: new Date(),
        updatedAt: new Date(),
        missionMeta: workflow.missionMeta ?? {
          allowDynamicMap: true,
          mapLocked: false,
          flowOrientation: 'auto',
          flowTracingMode: false,
          smartAutoConfigureOnConnect: true,
          smartAutoFixPolicy: 'safe-and-review',
          smartInputModalBehavior: 'badge-only',
          truthFactorStrictness: 'balanced',
        },
      });
      const workflows = [...state.workflows, imported];
      guardedPersist(workflows);
      return { workflows, currentWorkflow: imported, selectedNode: null, connectFromNodeId: null, ...resetMapUndoState() };
    });
  },

  applyAssembledWorkflow: (assembled, options) => {
    if (options?.skipUndo !== true) get().recordMapUndoPoint();
    const persist = options?.persist !== false;
    let applied = false;

    set((state) => {
      const now = new Date();

      if (!state.currentWorkflow) {
        if (persist && !assertCanCreateMission(state.workflows.length)) return state;

        const created: Workflow = {
          ...assembled,
          id: persist ? generateId() : assembled.id || generateId(),
          createdAt: now,
          updatedAt: now,
        };
        applied = true;
        const workflows = persist ? [...state.workflows, created] : state.workflows;
        if (persist) guardedPersist(workflows);
        return {
          workflows,
          currentWorkflow: created,
          selectedNode: null,
          connectFromNodeId: null,
        };
      }

      const currentWorkflow: Workflow = {
        ...state.currentWorkflow,
        name: assembled.name,
        nodes: assembled.nodes,
        edges: assembled.edges,
        missionMeta: assembled.missionMeta
          ? { ...state.currentWorkflow.missionMeta, ...assembled.missionMeta }
          : state.currentWorkflow.missionMeta,
        updatedAt: now,
      };

      applied = true;
      const next = syncCurrentWorkflow(state.workflows, currentWorkflow);
      if (persist && assertCanSaveFlows()) {
        guardedPersist(next.workflows);
      }
      return {
        ...next,
        selectedNode: null,
        connectFromNodeId: null,
      };
    });

    return applied;
  },

  applyAssembledWorkflowIfCurrent: (expectedWorkflowId, assembled, options) => {
    const { currentWorkflow } = get();
    if (expectedWorkflowId && currentWorkflow?.id !== expectedWorkflowId) {
      return false;
    }
    return get().applyAssembledWorkflow(assembled, options);
  },

  addNode: (node) => {
    get().recordMapUndoPoint();
    set((state) => {
      if (!state.currentWorkflow) return state;
      const currentWorkflow: Workflow = {
        ...state.currentWorkflow,
        nodes: [...state.currentWorkflow.nodes, node],
        updatedAt: new Date(),
      };
      return syncCurrentWorkflow(state.workflows, currentWorkflow);
    });
  },

  updateNode: (id, updates) => {
    const isPositionOnly = Object.keys(updates).every((k) => k === 'x' || k === 'y');
    if (!isPositionOnly) get().recordMapUndoPoint();

    set((state) => {
      if (!state.currentWorkflow) return state;

      const { x, y, ...configUpdates } = updates;
      const currentWorkflow: Workflow = {
        ...state.currentWorkflow,
        updatedAt: new Date(),
        nodes: state.currentWorkflow.nodes.map((node) => {
          if (node.id !== id) return node;

          return {
            ...node,
            ...(x !== undefined ? { x: Number(x) } : {}),
            ...(y !== undefined ? { y: Number(y) } : {}),
            ...(Object.keys(configUpdates).length > 0
              ? { config: { ...node.config, ...configUpdates } }
              : {}),
          };
        }),
      };

      return syncCurrentWorkflow(state.workflows, currentWorkflow);
    });
  },

  deleteNode: (id) => {
    get().recordMapUndoPoint();
    set((state) => {
      if (!state.currentWorkflow) return state;
      const currentWorkflow: Workflow = {
        ...state.currentWorkflow,
        updatedAt: new Date(),
        nodes: state.currentWorkflow.nodes.filter((n) => n.id !== id),
        edges: state.currentWorkflow.edges.filter((e) => e.from !== id && e.to !== id),
      };
      return { ...syncCurrentWorkflow(state.workflows, currentWorkflow), selectedNode: null };
    });
  },

  addEdge: (from, to) => {
    get().recordMapUndoPoint();
    set((state) => {
      if (!state.currentWorkflow) return state;
      const currentWorkflow: Workflow = {
        ...state.currentWorkflow,
        updatedAt: new Date(),
        edges: [...state.currentWorkflow.edges, { id: generateId(), from, to }],
      };
      return syncCurrentWorkflow(state.workflows, currentWorkflow);
    });
  },

  deleteEdge: (id) => {
    get().recordMapUndoPoint();
    set((state) => {
      if (!state.currentWorkflow) return state;
      const currentWorkflow: Workflow = {
        ...state.currentWorkflow,
        updatedAt: new Date(),
        edges: state.currentWorkflow.edges.filter((e) => e.id !== id),
      };
      return syncCurrentWorkflow(state.workflows, currentWorkflow);
    });
  },

  selectNode: (id) => {
    set({ selectedNode: id });
  },

  beginConnect: (fromNodeId) => {
    set({ connectFromNodeId: fromNodeId, selectedNode: fromNodeId });
  },

  cancelConnect: () => {
    set({ connectFromNodeId: null });
  },

  completeConnect: (toNodeId) => {
    const { connectFromNodeId, currentWorkflow } = get();
    if (!connectFromNodeId || !currentWorkflow) return null;
    if (connectFromNodeId === toNodeId) return null;

    const existing = currentWorkflow.edges.find(
      (e) => e.from === connectFromNodeId && e.to === toNodeId
    );

    if (existing) {
      get().deleteEdge(existing.id);
      set({ connectFromNodeId: null });
      return 'unlinked';
    }

    get().recordMapUndoPoint();
    set((state) => {
      if (!state.currentWorkflow) return state;
      const withEdge: Workflow = {
        ...state.currentWorkflow,
        updatedAt: new Date(),
        edges: [...state.currentWorkflow.edges, { id: generateId(), from: connectFromNodeId, to: toNodeId }],
      };
      const shouldAutoConfigure = state.currentWorkflow.missionMeta?.smartAutoConfigureOnConnect !== false;
      const configured = shouldAutoConfigure
        ? autoConfigureWorkflowNodes(withEdge, [connectFromNodeId, toNodeId]).workflow
        : withEdge;
      return {
        ...syncCurrentWorkflow(state.workflows, configured),
        connectFromNodeId: null,
      };
    });
    return 'linked';
  },

  autoConfigureNode: (nodeId) => {
    const { currentWorkflow } = get();
    if (!currentWorkflow) return [];
    const result = autoConfigureWorkflowNodes(currentWorkflow, [nodeId]);
    if (result.changes.length === 0) return [];
    get().recordMapUndoPoint();
    set((state) => {
      if (!state.currentWorkflow) return state;
      const next: Workflow = {
        ...result.workflow,
        id: state.currentWorkflow.id,
        createdAt: state.currentWorkflow.createdAt,
        updatedAt: new Date(),
      };
      return syncCurrentWorkflow(state.workflows, next);
    });
    return result.changes;
  },

  saveWorkflow: () => {
    if (!assertCanSaveFlows()) return;
    const { workflows, currentWorkflow } = get();
    if (!currentWorkflow) return;
    const updated = workflows.map((w) =>
      w.id === currentWorkflow.id ? { ...currentWorkflow, updatedAt: new Date() } : w
    );
    guardedPersist(updated);
    set({ workflows: updated, currentWorkflow: { ...currentWorkflow, updatedAt: new Date() } });
  },

  updateWorkflowName: (name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    get().recordMapUndoPoint();
    set((state) => {
      if (!state.currentWorkflow) return state;
      const currentWorkflow: Workflow = {
        ...state.currentWorkflow,
        name: trimmed,
        updatedAt: new Date(),
      };
      return syncCurrentWorkflow(state.workflows, currentWorkflow);
    });
  },

  updateMissionMeta: (patch) => {
    set((state) => {
      if (!state.currentWorkflow) return state;
      const nextMeta = { ...state.currentWorkflow.missionMeta, ...patch };
      if (patch.mapLocked === true) {
        nextMeta.allowDynamicMap = false;
      }
      const currentWorkflow: Workflow = {
        ...state.currentWorkflow,
        missionMeta: nextMeta,
        updatedAt: new Date(),
      };
      return syncCurrentWorkflow(state.workflows, currentWorkflow);
    });
  },

  applyFlowLayout: (orientation, options) => {
    if (options?.recordUndo !== false) get().recordMapUndoPoint();
    set((state) => {
      if (!state.currentWorkflow || state.currentWorkflow.nodes.length === 0) return state;
      const nodes = formatMapFlowLayout(state.currentWorkflow, orientation, {
        mode: options?.mode,
        fromOrientation: options?.fromOrientation,
      });
      const currentWorkflow: Workflow = {
        ...state.currentWorkflow,
        nodes,
        updatedAt: new Date(),
      };
      return syncCurrentWorkflow(state.workflows, currentWorkflow);
    });
  },

  executeWorkflow: async (signal, options) => {
    if (!assertCanRunFlows()) {
      throw new Error('Running flows requires an active plan');
    }
    const { currentWorkflow } = get();
    if (!currentWorkflow) {
      throw new Error('No workflow selected');
    }
    const executor = new WorkflowExecutor(currentWorkflow, { ...options, signal });
    return executor.execute();
  },
}));
